/**
 * Date formatting utilities for consistent date display across the application
 */

/**
 * Helper function to format time units with proper pluralization
 */
function formatTimeUnit(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
}

/**
 * Format a date as a relative time string (e.g., "2 days ago", "1 week ago")
 */
export function formatRelativeDate(date: Date | string | number): string {
  const now = new Date();
  const targetDate = new Date(date);
  const diffInMs = now.getTime() - targetDate.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInMinutes < 60) return formatTimeUnit(diffInMinutes, 'minute');
  if (diffInHours < 24) return formatTimeUnit(diffInHours, 'hour');
  if (diffInDays < 7) return formatTimeUnit(diffInDays, 'day');
  if (diffInWeeks < 4) return formatTimeUnit(diffInWeeks, 'week');
  if (diffInMonths < 12) return formatTimeUnit(diffInMonths, 'month');

  return formatTimeUnit(diffInYears, 'year');
}

/**
 * Format a date as a short date string (e.g., "Dec 15, 2024")
 */
export function formatShortDate(date: Date | string | number): string {
  const targetDate = new Date(date);

  return targetDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date as a full date and time string (e.g., "Dec 15, 2024 at 2:30 PM")
 */
export function formatFullDateTime(date: Date | string | number): string {
  const targetDate = new Date(date);

  return targetDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a date with both absolute and relative information (e.g., "Dec 15, 2024 (2 days ago)")
 */
export function formatDateWithRelative(date: Date | string | number): string {
  const shortDate = formatShortDate(date);
  const relativeDate = formatRelativeDate(date);

  return `${shortDate} (${relativeDate})`;
}
